/**
 * text-field.js
 */

import React from 'react';
import { 
  TextField as MuiTextField,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import get from 'lodash.get';
import { shouldRenderMobileLayout } from 'utils/app-context';


export default function TextField(props) {
  const {
    formContext: formContextProp,
    name,
    rules,
    helperText,
    defaultValue,
    onChange,
    onFocus,
    onBlur,
    doFocus,
    setValueAs,
    displayValueAs,
    disableErrorMessage,
    focusHandlers,
    type,
    multiline,
    dataTestId,
    ...rest
  } = props;
  
  const formContextDefault = useFormContext();
  const formContext = formContextProp || formContextDefault;
  const error = get(formContext?.formState.errors, name);
  const [hasFocus, setHasFocus] = React.useState(false);


  // mechanism for manually setting focus. 
  // in parent, pass doFocus param set to 0 by default and set to increment prev to trigger a focus:
  // setDoFocus(p => p + 1)
  React.useEffect(() => {
    if (doFocus) {
      setTimeout(() => formContext.setFocus(name));
    }
  // only focus when doFocus incremements
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doFocus]);


  // Prevent Numbers from incrementing/decrementing when scrolled over
  const preventScrollInputWhileFocussed = React.useCallback((e) => {
    if( hasFocus ) {
      e.target.blur();
      setTimeout(() => e.target.focus());
    }
  }, [hasFocus]);

  
  return (
    <Controller
      control={formContext.control}
      name={name}
      rules={rules}
      defaultValue={defaultValue}
      render={({ field: { ref, value, onChange, ...inputProps } }) => {
        const displayValue = displayValueAs ? displayValueAs(value) : value;
        return (
          <MuiTextField
            inputRef={ref}
            value={displayValue === null || displayValue === undefined ? '' : displayValue}
            onChange={e => {
              onChange(setValueAs ? setValueAs(e.target.value) : e.target.value);
              if (props.onChange) props.onChange(e);
            }}
            {...inputProps}
            variant="outlined"
            size="small"
            rows={multiline 
              ? shouldRenderMobileLayout() ? 12 : 10
              : null}
            multiline={multiline}
            // Workaround for a MUI bug, see https://github.com/mui/base-ui/issues/167#issuecomment-1987025654
            InputProps={multiline
              ? {
                rows: shouldRenderMobileLayout() ? 12 : 10,
                multiline: true,
                inputComponent: 'textarea'
              }
              : undefined
            }            
            error={Boolean(error)}
            helperText={(!disableErrorMessage ? error?.message : null) || helperText}
            onFocus={e => {
              if (focusHandlers) focusHandlers.onFocus(e);
              if (onFocus) onFocus(e);
              setHasFocus(true);
            }}
            onBlur={e => {
              inputProps.onBlur(e);
              if (focusHandlers) focusHandlers.onBlur(e);
              if (onBlur) onBlur(e);
              setHasFocus(false);
            }}
            onWheel={type && type.toLowerCase() === "number" ? preventScrollInputWhileFocussed : null}
            inputProps={{
              'data-testid': dataTestId ?? `${name}-input`
            }}
            type={type}
            {...rest}
          />
        );
      }}
    />
  );
}